import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
} from "@mui/material";
import { ApiHelper, UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import * as DialogContents from "components/Marketing/OTC/OTCDialogContents";
import { getOTCProduct } from "components/Marketing/OTC/OTCHelper";
import useOTCCalculations from "components/Marketing/OTC/useOTCCalculations";
import { useSnackbar } from "notistack";

import OTCHowItWorks from "./OTCHowItWorks";

export default function OTCDescriptionDialog({ open, onClose, row, producer_token }) {
  const [otcData, setOtcData] = useState(null);
  const [transactionData, setTransactionData] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const { globalState } = useContext(AppContext);
  const { enqueueSnackbar } = useSnackbar();

  const loadOTCPosition = useCallback(async () => {
    if (!row?.id) return;
    setLoading(true);

    const _otcPositionFilter = { producer_token: producer_token || globalState?.producer_token, id: row.id };
    if (!_otcPositionFilter.producer_token) {
      // Agent.
      delete _otcPositionFilter.producer_token;
    }

    try {
      const positions = await ApiHelper.callSmociObject(
        "data/DATANAC",
        "Markets",
        "OTCPositions",
        "SelectObject",
        _otcPositionFilter,
        globalState
      );
      const otcPosition = positions?.[0] || null;
      setOtcData(otcPosition);

      // Load corresponding UserTransactionHistory data
      if (otcPosition?.tracer_number) {
        await loadTransactionData(otcPosition.tracer_number);
      }
    } catch (err) {
      enqueueSnackbar(err.detail || err.message, { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [row, globalState, enqueueSnackbar, producer_token]);

  const loadTransactionData = useCallback(
    async (tracerNumber) => {
      try {
        const _transactionHistoryFilter = {
          tracer_number: tracerNumber,
          producer_token: producer_token || globalState?.producer_token,
          is_otc: 1,
          is_posted: true,
        };

        const _apiConfig =
          globalState?.agent_token && !globalState?.producer_token ? { agent_token: globalState?.agent_token } : {};

        if (!_transactionHistoryFilter.producer_token && globalState?.agent_token) {
          // Agent mode - remove producer_token
          delete _transactionHistoryFilter.producer_token;
          _transactionHistoryFilter.agent_token = globalState?.agent_token;
        }

        const transactionHistory = await UsersApiHelper.users_search(
          "UserTransactionHistory",
          _transactionHistoryFilter,
          _apiConfig
        );

        setTransactionData(transactionHistory?.[0] || null);
      } catch (err) {
        console.warn("Failed to load transaction data:", err);
        setTransactionData(null);
      }
    },
    [globalState, enqueueSnackbar, producer_token]
  );

  useEffect(() => {
    loadOTCPosition();
  }, [loadOTCPosition, open]);

  const product = getOTCProduct(otcData || {});

  const ContentComponent = useMemo(() => {
    return product ? DialogContents[`${product.component.replace(/\W/g, "")}Content`] : null;
  }, [product]);
  const calculation = useOTCCalculations(otcData);
  if (isLoading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (!otcData || !ContentComponent) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{otcData.contract_description}</DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          <ContentComponent otcData={otcData} calculations={calculation} transactionData={transactionData} />
          <OTCHowItWorks otcData={otcData} />
        </Grid>
      </DialogContent>
      <DialogActions>
        <Stack direction="row-reverse">
          <Button variant="contained" onClick={onClose}>
            Close
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
